import { getClient } from '@/api/client';
import type {
  AssignmentDtoApi,
  LogEntry,
  LoginResponse,
} from '@/types/domain';

export interface PodStatusResult {
  recordId: string;
  present: Record<string, boolean>;
}

const pad = (n: number) => String(n).padStart(2, '0');

function stringifyAdditionalFields(
  fields?: Record<string, string | number | null>,
): Record<string, string> | undefined {
  if (!fields) return undefined;
  return Object.fromEntries(
    Object.entries(fields)
      .filter(([, v]) => v != null && String(v).length > 0)
      .map(([k, v]) => [k, String(v)]),
  );
}

/**
 * Formats an absolute instant (ISO string) into the DEVICE-LOCAL date/time strings
 * FileMaker expects for log_L02_date (MM/dd/yyyy) and log_L03_time (HH:mm:ss).
 * These must reflect the driver's device clock at log creation, not server time.
 */
function deviceLocalDateTime(iso: string): { logDate: string; logTime: string } {
  const d = new Date(iso);
  return {
    logDate: `${pad(d.getMonth() + 1)}/${pad(d.getDate())}/${d.getFullYear()}`,
    logTime: `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`,
  };
}

/**
 * Hand-tuned typed wrappers around the middleware. Co-exists with the
 * generated OpenAPI client in src/api/generated/schema.d.ts; the
 * generated types are used for compile-time guarantees, while these
 * functions encapsulate URL conventions and response shaping.
 */
export const api = {
  async health(): Promise<{ status: string; version: string; timestamp: string }> {
    const r = await getClient().get('/health');
    return r.data;
  },

  async appVersion(): Promise<{
    current: string;
    minimum: string;
    serverVersion?: string;
    /** Per-tenant feature toggles (middleware 1.4.15+); absent on older servers. */
    features?: { removeFromDeliveryList?: boolean };
  }> {
    const r = await getClient().get('/app-version');
    return r.data;
  },

  async login(driverNumber: string, password: string): Promise<LoginResponse> {
    const r = await getClient().post('/auth/login', { driverNumber, password });
    return r.data;
  },

  async validate(): Promise<{ valid: boolean; driverNumber: string }> {
    const r = await getClient().post('/auth/validate');
    return r.data;
  },

  async syncAssignments(lastSync?: string): Promise<{
    newAssignments: AssignmentDtoApi[];
    modifiedAssignments: AssignmentDtoApi[];
    /** rem_f04_IDs flipped to switch 75 (reassigned away); delete locally. */
    removedAssignmentIds?: string[];
    serverTimestamp: string;
  }> {
    const r = await getClient().get('/assignments/sync', {
      params: lastSync ? { lastSync } : {},
    });
    return r.data;
  },

  /**
   * End-to-end POD acknowledgment: returns, per assignment recordId, which
   * legal-artifact container fields are present on the Assignment record.
   * Legacy (pre-1.0.39): assignment-level flags never flip for signatures.
   */
  async podStatusBatch(recordIds: string[]): Promise<PodStatusResult[]> {
    const r = await getClient().post('/pod-status/batch', { recordIds });
    return (r.data?.results ?? []) as PodStatusResult[];
  },

  /**
   * End-to-end POD acknowledgment against the MOBILE_LOG_MEDIA records the
   * uploads actually targeted (middleware 1.4.14+): per log-media recordId,
   * which container fields FileMaker confirms as present.
   */
  async podMediaStatus(
    items: Array<{ recordId: string; fields: string[] }>,
  ): Promise<PodStatusResult[]> {
    const r = await getClient().post('/pod-status/media', { items });
    return (r.data?.results ?? []) as PodStatusResult[];
  },

  async pushLogBatch(entries: LogEntry[]): Promise<{
    results: Array<{ clientId: string; success: boolean; fmRecordId?: string; error?: string }>;
    serverTimestamp: string;
  }> {
    const r = await getClient().post('/log/batch', {
      entries: entries.map((e) => {
        const { logDate, logTime } = deviceLocalDateTime(e.capturedAt);
        return {
          clientId: e.id,
          logType: e.logType,
          display: e.display,
          assignmentRecordId: e.assignmentId,
          tag: e.tag,
          gpsLat: e.latitude != null ? String(e.latitude) : undefined,
          gpsLng: e.longitude != null ? String(e.longitude) : undefined,
          logDate,
          logTime,
          capturedAt: e.capturedAt,
          additionalFields: stringifyAdditionalFields(e.additionalFields),
        };
      }),
    });
    return r.data;
  },

  async pushLog(entry: LogEntry): Promise<{
    clientId?: string;
    serverRecordId?: string;
    fmRecordId?: string;
    timestamp: string;
  }> {
    const { logDate, logTime } = deviceLocalDateTime(entry.capturedAt);
    const r = await getClient().post('/log', {
      clientId: entry.id,
      logType: entry.logType,
      display: entry.display,
      assignmentRecordId: entry.assignmentId,
      tag: entry.tag,
      gpsLat: entry.latitude != null ? String(entry.latitude) : undefined,
      gpsLng: entry.longitude != null ? String(entry.longitude) : undefined,
      logDate,
      logTime,
      capturedAt: entry.capturedAt,
      additionalFields: stringifyAdditionalFields(entry.additionalFields),
    });
    return r.data;
  },

  async ackBatch(assignmentIds: string[]): Promise<{
    results: Array<{ assignmentId: string; success: boolean; error?: string }>;
  }> {
    const r = await getClient().post('/ack/batch', { assignmentIds });
    return r.data;
  },

  async ackComplete(assignmentId: string): Promise<{ message: string; assignmentId: string }> {
    const r = await getClient().post(`/ack/${assignmentId}/complete`);
    return r.data;
  },

  async heartbeat(payload: object): Promise<void> {
    await getClient().post('/heartbeat', payload);
  },

  async pushPublicKey(): Promise<{ publicKey: string }> {
    const r = await getClient().get('/push/public-key');
    return r.data;
  },

  async pushSubscribe(sub: { endpoint: string; p256dh: string; auth: string }): Promise<void> {
    await getClient().post('/push/subscribe', sub);
  },

  async pushUnsubscribe(endpoint: string): Promise<void> {
    await getClient().post('/push/unsubscribe', { endpoint });
  },

  async uploadMedia(
    fmRecordId: string,
    fieldName: string,
    blob: Blob,
    filename: string,
    opts?: { latitude?: number | null; longitude?: number | null },
  ): Promise<{ message: string; fmRecordId: string; fieldName: string }> {
    const form = new FormData();
    form.append('file', blob, filename);
    if (opts?.latitude != null && opts?.longitude != null) {
      form.append('gpsLat', String(opts.latitude));
      form.append('gpsLng', String(opts.longitude));
    }
    const r = await getClient().post(`/log-media/${fmRecordId}/${fieldName}`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      timeout: 60_000,
    });
    return r.data;
  },
};
